import { fetchAuthSession } from '@backoffice-frontend/authentication'
import {
  useMoiaErrorNotification,
  useMoiaSuccessNotification,
} from '@backoffice-frontend/patterns'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const TOKEN_REFRESHED_QUERY_PARAM = 'tokenRefreshed'

// Checks the URL for the token refreshed query param and shows a toast if present
// We need to extract this into a separate hook because the button itself won't always be mounted
export const useRefreshPermissionsSuccessToast = () => {
  const { t } = useTranslation()
  const { enqueueMoiaSuccessNotification } = useMoiaSuccessNotification()

  const hasTokenRefreshedQueryParam = new URL(
    window.location.href,
  ).searchParams.get(TOKEN_REFRESHED_QUERY_PARAM)

  if (hasTokenRefreshedQueryParam === 'true') {
    // remove the query param from the URL to avoid showing the toast again on next reload
    const url = new URL(window.location.href)
    url.searchParams.delete(TOKEN_REFRESHED_QUERY_PARAM)
    window.history.replaceState({}, document.title, url.toString())

    enqueueMoiaSuccessNotification(
      t('userManagementUI_refreshPermissionsSuccess_toast'),
    )
  }
}

type RefreshPermissionsOptions = {
  skipSuccessToast?: boolean
}

export const useRefreshPermissions = () => {
  const { t } = useTranslation()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()

  const refreshPermissions = useCallback(
    async ({ skipSuccessToast = false }: RefreshPermissionsOptions = {}) => {
      try {
        await fetchAuthSession({ forceRefresh: true })

        const url = new URL(window.location.href)
        if (!skipSuccessToast) {
          url.searchParams.set(TOKEN_REFRESHED_QUERY_PARAM, 'true')
        }
        window.location.href = url.toString()
      } catch {
        enqueueMoiaErrorNotification(
          t('userManagementUI_refreshPermissionsError_toast'),
        )
      }
    },
    [enqueueMoiaErrorNotification, t],
  )

  return refreshPermissions
}
